/**
 * What did the server actually hand us?
 *
 * EAA-1501: a learner saw "Could not open this PDF file." on a `documents` question.
 * That message comes from pdf.js failing to *parse* the bytes, so it was read as "the
 * PDF is broken" — but the bytes had never been a PDF. When a protected `/media/...`
 * path cannot be signed (see `resolveMediaUrlsForViewer`), the viewer falls back to
 * fetching the raw path; an SPA or CDN host answers an unknown path with `200` and its
 * own `index.html`, so `response.ok` is true, `arrayBuffer()` succeeds, and the first
 * component able to notice anything wrong is the parser — which can only describe the
 * problem in terms of the format it expected.
 *
 * Sniffing the payload before it reaches a parser lets the UI separate "the server did
 * not return this document" from "this document is damaged", and lets the console say
 * which one it was. It is a diagnostic, not a security check.
 */

export type DocumentPayloadKind =
    /** `%PDF-` header. */
    | 'pdf'
    /** `PK\x03\x04` — OOXML (.docx/.xlsx/.pptx) and any other zip. */
    | 'zip'
    /** `D0 CF 11 E0` — legacy OLE compound file (.doc/.xls/.ppt). */
    | 'ole'
    /** An HTML document: almost always an error page, a login page, or an SPA shell. */
    | 'html'
    /** A JSON body: almost always an API error envelope. */
    | 'json'
    /** Zero bytes. */
    | 'empty'
    /** Something binary we do not recognise. */
    | 'unknown';

/**
 * pdf.js scans this far into the buffer for the `%PDF-` header before giving up, so a
 * file with a short preamble is still a valid PDF and must not be reported as HTML.
 */
const PDF_HEADER_SEARCH_BYTES = 1024;

/** Enough to see a doctype, an XML prolog, or leading whitespace before either. */
const TEXT_SNIFF_BYTES = 512;

const HTML_MARKERS = ['<!doctype html', '<html', '<head', '<body', '<!-- '];

function asciiPrefix(bytes: Uint8Array, length: number): string {
    let out = '';
    const end = Math.min(bytes.length, length);
    for (let i = 0; i < end; i += 1) {
        out += String.fromCharCode(bytes[i]);
    }
    return out;
}

function startsWithBytes(bytes: Uint8Array, signature: readonly number[]): boolean {
    if (bytes.length < signature.length) {
        return false;
    }
    return signature.every((byte, i) => bytes[i] === byte);
}

/** Classify a downloaded document body by its leading bytes. */
export function sniffDocumentPayload(data: ArrayBuffer): DocumentPayloadKind {
    if (data.byteLength === 0) {
        return 'empty';
    }

    const bytes = new Uint8Array(data, 0, Math.min(data.byteLength, PDF_HEADER_SEARCH_BYTES));

    if (asciiPrefix(bytes, PDF_HEADER_SEARCH_BYTES).includes('%PDF-')) {
        return 'pdf';
    }
    if (startsWithBytes(bytes, [0x50, 0x4b, 0x03, 0x04])) {
        return 'zip';
    }
    if (startsWithBytes(bytes, [0xd0, 0xcf, 0x11, 0xe0])) {
        return 'ole';
    }

    // Strip a UTF-8 BOM and leading whitespace before looking at text markers.
    let text = asciiPrefix(bytes, TEXT_SNIFF_BYTES);
    if (text.charCodeAt(0) === 0xef && text.charCodeAt(1) === 0xbb && text.charCodeAt(2) === 0xbf) {
        text = text.slice(3);
    }
    const trimmed = text.replace(/^\s+/, '');
    const lower = trimmed.toLowerCase();

    if (HTML_MARKERS.some((marker) => lower.startsWith(marker))) {
        return 'html';
    }
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        return 'json';
    }
    return 'unknown';
}

/**
 * Is this payload definitely NOT the document we asked for?
 *
 * Deliberately narrow: it answers "yes" only for bodies that cannot be any of the
 * binary formats these viewers read — an HTML page, a JSON envelope, or nothing at all.
 * `'unknown'` is left to the real parser, which knows its format far better than a
 * four-byte check does; the point is to stop *misattributing* an error page, not to
 * pre-empt every parse failure.
 */
export function isNotADocumentPayload(kind: DocumentPayloadKind): boolean {
    return kind === 'html' || kind === 'json' || kind === 'empty';
}
